import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import DashboardFooter from '../components/DashboardFooter';
import { Quantum } from 'ldrs/react';
import { NotificationManager } from 'react-notifications';

const AddressCell = ({ address }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });

  const handleMouseEnter = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPos({
      top: rect.bottom + window.scrollY + 8, // show slightly below cell
      left: rect.left + window.scrollX, // align horizontally
    });
    setShowTooltip(true);
  };

  const handleMouseLeave = () => setShowTooltip(false);

  return (
    <>
      <div
        className="address-cell"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <span className="truncated-address">{address || '-'}</span>
      </div>

      {showTooltip &&
        ReactDOM.createPortal(
          <div
            className="address-tooltip"
            style={{
              top: tooltipPos.top,
              left: tooltipPos.left,
              position: 'absolute',
            }}
          >
            {address}
          </div>,
          document.body
        )}
    </>
  );
};

const ManualPayments = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [updating, setUpdating] = useState(null);

  const fetchAppointments = async (dateToFetch = dateFilter) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      const url = `${process.env.REACT_APP_HOST_API_URL}/admin-user/all-appointments-amount/`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ date: dateToFetch ? `${dateToFetch}T00:00:00` : null }),
      });

      if (!response.ok) throw new Error(`Data not found (${response.status})`);
      const data = await response.json();

      const withAmounts = (data.results || []).map((a) => ({
        ...a,
        amount:
          a.amount !== null && a.amount !== undefined && a.amount !== 0
            ? a.amount
            : '',
        net_profit_amount:
          a.net_profit_amount !== null &&
            a.net_profit_amount !== undefined &&
            a.net_profit_amount !== 0
            ? a.net_profit_amount
            : '',
      }));

      setAppointments(withAmounts);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAmountChange = (id, value) => {
    setAppointments((prev) =>
      prev.map((a) => (a.id === id ? { ...a, amount: value } : a))
    );
  };

  const handleNetProfitChange = (id, value) => {
    setAppointments((prev) =>
      prev.map((a) => (a.id === id ? { ...a, net_profit_amount: value } : a))
    );
  };


  const handleUpdate = async (id, amount, net_profit_amount) => {
    try {
      setUpdating(id);
      const token = localStorage.getItem('token');
      if (!token) return;

      const url = `${process.env.REACT_APP_HOST_API_URL}/admin-user/all-appointments-amount/`;

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id,
          amount: amount ? Number(amount) : 0,
          net_profit_amount: net_profit_amount ? Number(net_profit_amount) : 0,
        }),
      });

      if (!response.ok) throw new Error(`Update failed (${response.status})`);
      await response.json();

      setAppointments((prev) =>
        prev.map((a) =>
          a.id === id
            ? { ...a, amount: amount, net_profit_amount: net_profit_amount }
            : a
        )
      );
      NotificationManager.success('Amount and net profit updated successfully!');
    } catch (error) {
      console.error('Error updating amount:', error);
      NotificationManager.error('Failed to update amount.');
    } finally {
      setUpdating(null);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, []);

  useEffect(() => {
    fetchAppointments(dateFilter);
  }, [dateFilter]);

  const filteredAppointments = appointments.filter((a) => {
    const matchesSearch =
      (a.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(a.mobile || '').includes(searchTerm);

    const matchesDate =
      !dateFilter || (a.start_from && a.start_from.split('T')[0] === dateFilter);

    return matchesSearch && matchesDate;
  });

  if (loading)
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{
          minHeight: 'calc(100vh - 40px)',
          width: '100vw',
          marginLeft: '-280px',
          padding: '20px',
        }}
      >
        <Quantum size="45" speed="1.75" color="blue" />
      </div>
    );

  return (
    <div
      className="d-flex flex-column min-vh-100 pb-2 px-3"
      style={{ backgroundColor: '#f8f9fa' }}
    >
      <div className="flex-grow-1 mb-4">
        <div className="container-fluid px-2 px-md-4" style={{ paddingTop: '20px' }}>
          <div
            className="row mb-4 bg-white shadow-sm border-bottom sticky-top py-3 px-md-3 px-2 rounded"
            style={{
              zIndex: 1050,
              background: 'linear-gradient(135deg, #0564bf, #89CFF0)',
            }}
          >
            <div className="col-12">
              <h3 className="text-light mb-1">Payment Details</h3>
            </div>
            <p className="text-light mb-0">
              Manage and review all manually processed payments.
            </p>

            <div
              className="d-flex justify-content-between align-items-center flex-wrap mt-3"
              style={{ gap: '0.75rem' }}
            >
              <div className="d-flex gap-4">
                <input
                  type="date"
                  className="form-control"
                  style={{ maxWidth: '180px' }}
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                />
                <input
                  type="search"
                  placeholder="Search by name or mobile"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    minWidth: '260px',
                    border: 'none',
                    outline: 'none',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  }}
                />
              </div>
            </div>
          </div>

          <div className="table-responsive shadow-sm rounded bg-white mt-5">
            <table className="table mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th>Name</th>
                  <th>Mobile No.</th>
                  <th>Address</th>
                  <th>Time</th>
                  <th>Amount</th>
                  <th>Net Profit</th>
                  <th>Update</th>
                </tr>
              </thead>
              <tbody>
                {filteredAppointments.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-3 text-muted">
                      No manual payments found.
                    </td>
                  </tr>
                ) : (
                  filteredAppointments.map((a) => (
                    <tr key={a.id}>
                      <td>{a.name || '-'}</td>
                      <td>{a.mobile || '-'}</td>
                      <td style={{ maxWidth: '220px' }}>
                        <AddressCell address={a.address} />
                      </td>
                      <td>
                        {a.start_from && a.end_at
                          ? `${new Date(a.start_from).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })} - ${new Date(a.end_at).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}`
                          : '-'}
                      </td>
                      <td style={{ width: '150px' }}>
                        <input
                          type="number"
                          value={a.amount !== '' ? a.amount : ''}
                          onChange={(e) => handleAmountChange(a.id, e.target.value)}
                          className="form-control form-control-sm"
                          placeholder="Enter amount"

                        />
                      </td>
                      {/* Net Profit Field */}
                      <td style={{ width: '150px' }}>
                        <input
                          type="number"
                          value={a.net_profit_amount || ''}
                          onChange={(e) => handleNetProfitChange(a.id, e.target.value)}
                          className="form-control form-control-sm"
                          placeholder="Enter net profit"
                        />
                      </td>
                      <td>
                        
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => handleUpdate(a.id, a.amount, a.net_profit_amount)}
                          disabled={updating === a.id}
                        >
                          Update
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <DashboardFooter />
    </div>
  );
};

export default ManualPayments;
